import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges
} from '@angular/core';

import { Subject } from 'rxjs/Subject';
import { TwoFaAuthenticationDataService } from '@services/user/two-fa-authentication.data.service';
import {
	TwoFactorAuthorization,
	TwoFaModalControl
} from '@models/two-fa.models';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { MessageService } from '@services/helpers/message-service/message.service';
import { SNModalService } from '@web-services/modal/snModal.service';
import { ConfirmGeneralModalComponent } from '@web-components/common/confirm-general-modal/confirm-general-modal.component';

@Component({
	selector: 'two-fa-settings',
	template: require('./two-fa-settings.html'),
	styles: [require('./two-fa-settings.scss')]
})
export class TwoFaSettingsComponent implements OnInit, OnChanges, OnDestroy {
	@Input() twoFactorSettings: TwoFactorAuthorization;
	@Output()
	updateUserProfile: EventEmitter<boolean> = new EventEmitter<boolean>();

	requestProcessed: boolean;
	enableToggle = false;
	forceAllToggle = false;

	private unsubscribe: Subject<void> = new Subject();

	constructor(
		private twoFactorAuthenticationService: TwoFaAuthenticationDataService,
		private messageService: MessageService,
		private modalService: SNModalService
	) {}

	ngOnInit(): void {
		this.listenToTwoFaModalActions();
	}

	ngOnChanges(changes: SimpleChanges): void {
		this.requestProcessed = false;
		this.setCorrectToggleStates();
	}

	ngOnDestroy(): void {
		this.unsubscribe.next();
		this.unsubscribe.complete();
	}

	enableToggleChange(state: boolean, enforce: boolean): void {
		if (state) {
			this.twoFactorAuthenticationService.setTwoFaModalControlState({
				state: true,
				action: 'SETUP',
				payload: {
					enforce,
					user2faState: this.twoFactorSettings.user2faState,
					loginActions: false
				}
			});
		} else {
			const {
				method,
				alertMessage
			} = this.twoFactorSettings.user2faState.actions.TURN_OFF_2FA;

			if (method === 'ALERT_MODAL') {
				const { title, message } = alertMessage;
				this.openAlertModal(title, message);
				return;
			}
			this.confirmTwoFaDisable();
		}
		this.enableToggle = state;
	}

	forceAllToggleChange(state: boolean): void {
		if (state) {
			this.confirmEnforceEnable();
		} else {
			this.switchOffEnforce();
		}
		this.forceAllToggle = state;
	}

	private listenToTwoFaModalActions(): void {
		this.twoFactorAuthenticationService.twoFaModalControlState
			.pipe(
				takeUntil(this.unsubscribe),
				filter((data: TwoFaModalControl): boolean => data && data.state)
			)
			.subscribe((data: TwoFaModalControl): void => {
				if (data.action === 'DISMISS_SETUP') {
					this.enableToggle = false;
					if (data.payload.enforce) {
						this.forceAllToggle = false;
					}
				}

				if (data.action === 'SUCCESS_SETUP') {
					this.updateUserProfile.emit(true);
				}
			});
	}

	private setCorrectToggleStates(): void {
		this.enableToggle = this.twoFactorSettings.user2faState
			? !!this.twoFactorSettings.user2faState.actions.TURN_OFF_2FA
			: false;

		this.forceAllToggle = this.twoFactorSettings.account2faSettings
			? !!this.twoFactorSettings.account2faSettings.actions.MAKE_2FA_OPTIONALLY
			: false;
	}

	private switchOff2Fa(): void {
		const {
			url,
			method
		} = this.twoFactorSettings.user2faState.actions.TURN_OFF_2FA;

		this.requestProcessed = true;
		this.twoFactorAuthenticationService
			.send2FAAction(url, method)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(): void => {
					this.messageService.showToaster('s_1', 'twoFa');
					this.updateUserProfile.emit(true);
				},
				({ error }): void => {
					this.enableToggle = true;
					this.showBackendError(error);
				}
			);
	}

	private enforce2FaForAllUsers(): void {
		const {
			url,
			method,
			alertMessage
		} = this.twoFactorSettings.account2faSettings.actions.MAKE_2FA_MANDATORY;

		if (method === 'ALERT_MODAL') {
			const { title, message } = alertMessage;
			this.openAlertModal(title, message);
			this.forceAllToggle = false;
			return;
		}

		this.requestProcessed = true;
		this.twoFactorAuthenticationService
			.send2FAAction(url, method)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(): void => {
					this.updateUserProfile.emit(true);
					this.messageService.showToaster('s_2', 'twoFa');
				},
				({ error }): void => {
					this.forceAllToggle = false;
					this.showBackendError(error);
				}
			);
	}

	private switchOffEnforce(): void {
		const {
			url,
			method,
			alertMessage
		} = this.twoFactorSettings.account2faSettings.actions.MAKE_2FA_OPTIONALLY;

		if (method === 'ALERT_MODAL') {
			const { title, message } = alertMessage;
			this.openAlertModal(title, message);
			this.forceAllToggle = true;
			return;
		}
		this.requestProcessed = true;
		this.twoFactorAuthenticationService
			.send2FAAction(url, method)
			.pipe(takeUntil(this.unsubscribe))
			.subscribe(
				(): void => {
					this.updateUserProfile.emit(true);
					this.messageService.showToaster('s_3', 'twoFa');
				},
				({ error }): void => {
					this.forceAllToggle = true;
					this.showBackendError(error);
				}
			);
	}

	private showBackendError(error): void {
		const { errorTitle, errorMessage } = error;
		this.messageService.showErrorFromBackend(errorTitle, errorMessage);
	}

	private confirmTwoFaDisable(): void {
		const confirmModal = this.modalService.openModal(
			ConfirmGeneralModalComponent,
			{
				windowClass: 'ng-bootstrap-modal modal-visible modal-narrow'
			}
		);
		confirmModal.componentInstance.text =
			'Enabling two-factor authentication is more secure. Are you sure you want to turn this off?';
		confirmModal.componentInstance.buttons = [
			{
				text: 'Yes, turn it off',
				color: 'danger',
				size: 'lg',
				action: 'DISABLE_2FA'
			},
			{
				text: 'No, keep it on',
				color: 'success',
				size: 'lg',
				action: 'KEEP_2FA'
			}
		];

		confirmModal.result.then((action: string): void => {
			if (action === 'DISABLE_2FA') {
				this.switchOff2Fa();
			}

			if (action === 'KEEP_2FA') {
				this.enableToggle = true;
			}
		});
	}

	private confirmEnforceEnable(): void {
		const confirmModal = this.modalService.openModal(
			ConfirmGeneralModalComponent,
			{
				windowClass: 'ng-bootstrap-modal modal-visible modal-narrow'
			}
		);
		confirmModal.componentInstance.text =
			'This will make it compulsory for all other Users to set up their own two-factor authentication.';
		confirmModal.componentInstance.buttons = [
			{
				text: 'Yes, make it compulsory',
				color: 'success',
				size: 'lg',
				action: 'ENFORCE_2FA'
			},
			{
				text: 'No, leave it optional',
				color: 'default',
				size: 'lg',
				action: 'LEAVE_OPTIONAL'
			}
		];

		confirmModal.result.then((action: string): void => {
			if (action === 'ENFORCE_2FA') {
				if (this.twoFactorSettings.user2faState.actions.TURN_ON_2FA) {
					this.enableToggleChange(true, true);
				} else {
					this.enforce2FaForAllUsers();
				}
			}

			if (action === 'LEAVE_OPTIONAL') {
				this.forceAllToggle = false;
			}
		});
	}

	private openAlertModal(title: string, text: string): void {
		const alertModal = this.modalService.openModal(
			ConfirmGeneralModalComponent
		);
		alertModal.componentInstance.title = title;
		alertModal.componentInstance.text = text;

		alertModal.componentInstance.buttons = [
			{
				text: 'Got it',
				color: 'default',
				size: 'lg',
				action: 'CLOSE_ALERT'
			}
		];
		alertModal.result.then((): void => {
			if (this.twoFactorSettings.account2faSettings) {
				this.forceAllToggle = true;
			}
		});
	}
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./node_modules/string-replace-webpack-plugin/loader.js?id=os2z8rc99lf!./web/modern/components/settings/profile/two-fa-settings/two-fa-settings.ts